<template>
  <div class="screens">

    <header class="masthead">
      <h1 class="title">Screens<span class="dot">.</span></h1>
      <p class="sub">
        Wall displays paired with Vero. A paired screen stays signed in until it
        is revoked here; it does not depend on a Microsoft account.
      </p>
    </header>

    <!-- Pair -->
    <div class="form">
      <label class="field grow">
        <span class="field-label">Screen name</span>
        <input v-model="form.name" class="input" type="text" placeholder="DK hallway">
      </label>

      <label class="field">
        <span class="field-label">Restrict to IP <span class="opt">optional</span></span>
        <input v-model="form.allowedIp" class="input" type="text" placeholder="Office public IP">
      </label>

      <button class="btn primary" :disabled="!form.name.trim() || minting" @click="mint">
        {{ minting ? 'Creating…' : 'Pair a screen' }}
      </button>
    </div>

    <p v-if="formError" class="msg err">{{ formError }}</p>

    <!-- The code, shown once -->
    <div v-if="pending" class="code-card">
      <p class="code-label">On the screen, open <strong>{{ pairUrl }}</strong> and enter</p>
      <p class="code">{{ spaced(pending.code) }}</p>
      <p class="code-foot">
        For <strong>{{ pending.name }}</strong>. Expires in {{ remaining }}.
        <button class="link" @click="pending = null">Done</button>
      </p>
    </div>

    <!-- List -->
    <p v-if="loading" class="msg">Reading screens</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="screens.length === 0" class="msg">
      No screens paired yet. Create a code above and enter it on the display.
    </p>

    <table v-else class="table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Last seen</th>
          <th>From</th>
          <th>Paired by</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="s in screens" :key="s.id" :class="{ off: s.revoked }">
          <td class="name">
            {{ s.name }}
            <span v-if="s.revoked" class="tag">revoked</span>
            <span v-else-if="s.allowed_ip" class="tag">pinned to {{ s.allowed_ip }}</span>
          </td>
          <td>{{ s.last_seen_at || 'never' }}</td>
          <td class="mono">{{ s.last_seen_ip || '—' }}</td>
          <td>
            {{ s.paired_by }}
            <span class="when">{{ s.paired_at }}</span>
          </td>
          <td class="actions">
            <button v-if="!s.revoked" class="link danger" @click="revoke(s)">Revoke</button>
          </td>
        </tr>
      </tbody>
    </table>

  </div>
</template>


<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { createPairingCode, listScreens, revokeScreen } from '@/services/ScreenService'

const screens = ref([])
const loading = ref(true)
const error = ref(null)

const form = ref({ name: '', allowedIp: '' })
const formError = ref(null)
const minting = ref(false)

// The code just minted, with a countdown so nobody types one that has
// already expired and wonders why.
const pending = ref(null)
const secondsLeft = ref(0)
let countdown = null

const pairUrl = computed(() => `${window.location.origin}/pair`)

const remaining = computed(() => {
  const m = Math.floor(secondsLeft.value / 60)
  const s = secondsLeft.value % 60
  return `${m}:${String(s).padStart(2, '0')}`
})

function spaced(code) {
  return code.replace(/(.{4})/g, '$1 ').trim()
}

async function load() {
  loading.value = true
  error.value = null

  try {
    screens.value = await listScreens()
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function mint() {
  formError.value = null
  minting.value = true

  try {
    const result = await createPairingCode(form.value.name.trim(), form.value.allowedIp.trim())

    pending.value = { code: result.code, name: form.value.name.trim() }
    secondsLeft.value = result.expiresInSeconds

    clearInterval(countdown)
    countdown = setInterval(() => {
      secondsLeft.value = Math.max(0, secondsLeft.value - 1)
      if (secondsLeft.value === 0) {
        clearInterval(countdown)
        pending.value = null
      }
    }, 1000)

    form.value = { name: '', allowedIp: '' }
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  } finally {
    minting.value = false
  }
}

async function revoke(screen) {
  const ok = window.confirm(
    `Revoke ${screen.name}? It will stop working immediately and will need pairing again.`
  )
  if (!ok) return

  try {
    await revokeScreen(screen.id)
    await load()
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  }
}

onMounted(load)
onBeforeUnmount(() => clearInterval(countdown))
</script>


<style scoped>
.screens {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;

  max-width: 1000px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 24px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 8px 0 0;
  max-width: 62ch;
}

/* ---------- Form ---------- */

.form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 20px;
}

.field { display: flex; flex-direction: column; gap: 6px; }
.field.grow { flex: 1; min-width: 220px; }

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.opt { font-weight: 400; letter-spacing: 0; text-transform: none; }

.input {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: #191c1e;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  min-width: 200px;
}

.input:focus { border-color: var(--ink); }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:disabled { opacity: 0.4; cursor: default; }
.btn.primary { background: var(--ink); color: #fff; }
.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- The code ---------- */

.code-card {
  border: 2px solid var(--ink);
  border-radius: 14px;
  padding: 22px 26px;
  margin-bottom: 28px;
  text-align: center;
}

.code-label {
  font-size: 14px;
  color: var(--ink-soft);
  margin: 0 0 10px;
}

.code {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 40px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--ink);
  margin: 0 0 10px;
}

.code-foot {
  font-size: 13.5px;
  color: var(--ink-soft);
  margin: 0;
}

.code-foot .link { margin-left: 12px; }

/* ---------- Table ---------- */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14.5px;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 0 12px 10px 0;
  border-bottom: 1px solid var(--line);
}

.table td {
  padding: 12px 12px 12px 0;
  border-bottom: 1px solid #f1f4f7;
  vertical-align: middle;
}

tr.off td { opacity: 0.45; }

.name { font-weight: 600; color: var(--ink); }

.tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-soft);
  background: #f1f4f7;
  padding: 2px 8px;
  border-radius: 5px;
  margin-left: 8px;
}

.mono {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13px;
}

.when {
  display: block;
  font-size: 12px;
  color: #a3adb9;
}

.actions { text-align: right; }

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
  max-width: 60ch;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.link.danger { color: #b3261e; }

@media (max-width: 800px) {
  .screens { padding: 0 20px 60px; }
  .form { flex-direction: column; align-items: stretch; }
}
</style>
